/** SMS opt-in toggle — Phase 2D-sched-d.
 *
 * SMS delivery is metered (Twilio bills per send) so the org admin
 * explicitly opts in. When this is off, the invite modal's "Send by
 * SMS" checkbox is disabled with a hint pointing here. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Organization } from "@/lib/types";


export function SmsOptInCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await api.patch<Organization>(ApiPaths.orgSms, {
        sms_enabled: next,
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
    },
  });

  if (org.isLoading) return null;
  if (!org.data) return null;

  const enabled = org.data.sms_enabled;

  return (
    <section className="card space-y-2">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.sms.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.sms.cardHint")}</p>
      </header>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-sm text-nb-text">
          <span className="text-nb-text-2">{t("admin.sms.statusLabel")}</span>{" "}
          <span
            className={
              enabled
                ? "font-medium text-cvp-ok"
                : "font-medium text-nb-text-2"
            }
          >
            {enabled
              ? t("admin.sms.statusEnabled")
              : t("admin.sms.statusDisabled")}
          </span>
        </span>
        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => toggle.mutate(!enabled)}
          disabled={toggle.isPending}
        >
          {toggle.isPending
            ? t("admin.sms.saving")
            : enabled
              ? t("admin.sms.disableButton")
              : t("admin.sms.enableButton")}
        </button>
      </div>
      {toggle.isError && (
        <p className="text-xs text-cvp-danger">
          {(toggle.error as Error).message ?? t("admin.sms.saveErrorGeneric")}
        </p>
      )}
    </section>
  );
}
